import { isForeignEnvironment, quit } from '@/lib/pwa';
import { CBCC_EVENT, trackCbccEvent } from '../services/analytics';

// Where a back press from the CVP page goes, and the tracking that must ride
// along with it. Ported from fintech-ui's shared ixigoCredit `onBack` helper.
//
// This module is the SINGLE funnel for leaving the page backwards, and that is
// the point of it existing at all. Previously the in-page control called
// `backOrExit('exit')` directly and the DEVICE back press bypassed it entirely,
// so half the exits went untracked and the two paths could disagree about where
// the user should land. Both entry points now call `handleCbccBack`.

const FINTECH = 'fintech';
/** `?source=MPP` — the user arrived from Manage Payment Options. */
const MPP_SOURCE = 'MPP';
const MANAGE_PAYMENTS_PATH = '/ixi-finance/manage-payments';
const FINTECH_HOME_PATH = '/finance/home';

/**
 * sessionStorage throws in Safari private mode rather than returning null, and
 * an exception here would strand the user on the page with a dead back button.
 * A missing key and an unreadable store mean the same thing to every branch
 * below: this entry point didn't set it.
 */
function readSession(key: string): string | null {
	try {
		return window.sessionStorage.getItem(key);
	} catch {
		return null;
	}
}

function clearSession(key: string): void {
	try {
		window.sessionStorage.removeItem(key);
	} catch {
		// Same as above — if the store is unreadable there is nothing to clear.
	}
}

/**
 * The GA half of the event. `trackCbccEvent` already reaches the ixigo tracker
 * (CleverTap) with the CVP commons — `User Status` and `Onboarding Eligibility`
 * come from `setCbccCommonProps`, which is what fintech-ui passed explicitly as
 * `cardScreenData`, so there is nothing to thread down here.
 *
 * GA takes the snake_cased name and deliberately no-ops outside a browser and
 * inside a foreign host webview (a native app's analytics is the host's own, not
 * the page's). Guarded on `dataLayer` existing: fin-apps has no GTM wired yet,
 * so this is inert today and starts working when GTM lands, with no change here
 * — same pattern as the REDIRECTED_TO_BANK push in use-cbcc-onboard.
 */
function trackGaBackpress(clientId: string): void {
	if (typeof window === 'undefined') return;
	if (isForeignEnvironment(clientId)) return;
	(window as typeof window & { dataLayer?: unknown[] }).dataLayer?.push({
		event: 'cbcc_cvp_page_backpress',
	});
}

/**
 * Where back goes, as a four-way branch on how the user got here. Order is the
 * contract — the checks are increasingly generic, so an earlier match is always
 * the more specific answer about where the user came from.
 */
export function cbccBack(clientId: string): void {
	if (typeof window === 'undefined') return;

	// Came from Manage Payment Options: a hard navigation, not a history pop —
	// MPP is a separate document and the CVP page may have been opened fresh.
	if (
		new URLSearchParams(window.location.search).get('source') === MPP_SOURCE
	) {
		window.location.assign(MANAGE_PAYMENTS_PATH);
		return;
	}

	// Entered from the fintech home tab. The key is consumed on the way out so a
	// later visit that arrives some other way doesn't inherit this destination.
	if (readSession('entryPoint') === FINTECH) {
		clearSession('entryPoint');
		window.location.replace(FINTECH_HOME_PATH);
		return;
	}

	// Navigated from elsewhere inside fintech, which means there IS a real
	// previous entry to pop.
	if (readSession('source') === FINTECH) {
		window.history.back();
		return;
	}

	// A foreign host embedded this page with no browser chrome, so the only way
	// "back" is to hand control to the app and let it close the webview. `quit()`
	// reports whether a bridge answered: a foreign clientId in a plain browser
	// tab (`?clientId=…` opened in Chrome) has none, and falls through rather
	// than leaving the button dead.
	if (isForeignEnvironment(clientId) && quit()) return;

	window.location.replace('/');
}

/**
 * Fire the Backpress event, then leave. Tracking goes FIRST: every branch below
 * navigates the document away, and an event queued after `location.replace` can
 * lose the race.
 */
export function handleCbccBack(clientId: string): void {
	trackCbccEvent(CBCC_EVENT.CVP_PAGE_BACKPRESS);
	trackGaBackpress(clientId);
	cbccBack(clientId);
}
